import { ObjectEntityFragment } from 'pages/facilities/api/fragments/generated/ObjectEntity.fragment';
import { useObject } from 'utils/api/useObject';
import BasicSection from './BasicSection';
import HrmsSection from './HrmsSection';
import MapSection from './MapSection';
import TimeSection from './TimeSection';
import { timezoneList } from 'utils/helpers/lists/timezoneList';
import PhotoGallery from './PhotoGallery';

interface MainInfoFacilityProps {
  values: any;
  touched: any;
  errors: any;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: (field: string, value: any) => void;
  onParentObjectChange: (value: string) => void;
  parentObjects: ObjectEntityFragment[];
  onParentObjectSearch: (value: string) => void;
  onParentObjectScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  isLoadingCreate?: boolean;
}

const MainInfoFacility = ({
  values,
  touched,
  errors,
  handleChange,
  setFieldValue,
  onParentObjectChange,
  parentObjects,
  onParentObjectSearch,
  onParentObjectScroll,
  isLoadingCreate,
}: MainInfoFacilityProps) => {
  const { object } = useObject();

  const isWorksheetEnabledInParents = (objects: ObjectEntityFragment[], targetId: string): boolean => {
    for (const obj of objects) {
      if (obj.id === targetId) {
        return (
          !!obj.settings?.isShowWorkSheetInThisObject ||
          (obj.parent ? isWorksheetEnabledInParents(objects, obj.parent.id) : false)
        );
      }
      if (obj.children) {
        const result = isWorksheetEnabledInParents(obj.children, targetId);
        if (result) return true;
      }
    }
    return false;
  };

  const isFieldInherited = (fieldName: string) => {
    if (!values.parentFacilityId || !parentObjects.length) return false;

    const findObjectInHierarchy = (objects: ObjectEntityFragment[], id: string): ObjectEntityFragment | null => {
      for (const obj of objects) {
        if (obj.id === id) return obj;
        if (obj.children) {
          const found = findObjectInHierarchy(obj.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const selectedObject = findObjectInHierarchy(parentObjects, values.parentFacilityId);
    if (!selectedObject) return false;

    switch (fieldName) {
      case 'cityId':
        return !!selectedObject.settings?.city?.id;
      case 'timezone':
        return !!selectedObject.settings?.timeZone;
      case 'naimixLegalEntityId':
        return !!selectedObject.settings?.naimixLegalEntity?.id;
      case 'hrmsId':
        return !!selectedObject.hrmsId;
      case 'showTimesheet':
        return selectedObject.id ? isWorksheetEnabledInParents(parentObjects, selectedObject.id) : false;
      default:
        return false;
    }
  };

  const handleParentObjectChange = (value: string) => {
    onParentObjectChange(value);

    const findObjectWithHierarchy = (objects: ObjectEntityFragment[], id: string): ObjectEntityFragment | null => {
      for (const obj of objects) {
        if (obj.id === id) return obj;
        if (obj.children) {
          const found = findObjectWithHierarchy(obj.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const objectWithHierarchy = findObjectWithHierarchy(parentObjects, value);

    if (objectWithHierarchy) {
      if (objectWithHierarchy.settings) {
        setFieldValue('cityId', objectWithHierarchy.settings.city?.id);
        const timezoneOption = timezoneList.find(
          option => Number(option.id) === objectWithHierarchy.settings?.timeZone,
        );
        setFieldValue('timezone', timezoneOption?.id);
        setFieldValue('checkInMeasurement', objectWithHierarchy.settings.checkInMeasurement);
        setFieldValue('checkOutMeasurement', objectWithHierarchy.settings.checkOutMeasurement);
        setFieldValue('naimixLegalEntityId', objectWithHierarchy.settings.naimixLegalEntity?.id);
      }
      setFieldValue('hrmsId', objectWithHierarchy.hrmsId?.toString());
    }
  };

  return (
    <div className="grid grid-cols-12 gap-5">
      <BasicSection
        values={values}
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        onParentObjectChange={handleParentObjectChange}
        parentObjects={parentObjects}
        onParentObjectSearch={onParentObjectSearch}
        onParentObjectScroll={onParentObjectScroll}
      />

      <div className="col-span-full border-t border-gray-200 my-2"></div>

      <HrmsSection
        values={values}
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        isFieldInherited={isFieldInherited}
      />

      <div className="col-span-full border-t border-gray-200 my-2"></div>

      <MapSection
        values={values}
        touched={touched}
        errors={errors}
        handleChange={handleChange}
        setFieldValue={setFieldValue}
        isFieldInherited={isFieldInherited}
      />

      <div className="col-span-full border-t border-gray-200 my-2"></div>

      <TimeSection
        values={values}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
        isFieldInherited={isFieldInherited}
      />

      <div className="col-span-full border-t border-gray-200 my-2"></div>

      <PhotoGallery />

      <div className="col-span-full border-t border-gray-200 my-2"></div>

      <div className="col-span-full flex justify-end">
        <button type="submit" className="btn-primary" disabled={isLoadingCreate}>
          {object ? (isLoadingCreate ? 'Сохранение...' : 'Сохранить') : isLoadingCreate ? 'Создание...' : 'Создать'}
        </button>
      </div>
    </div>
  );
};

export default MainInfoFacility;
